import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { of as ObservableOf } from 'rxjs/observable/of';
import { catchError, mergeMap } from 'rxjs/operators';
import {
  cloneDeep as _cloneDeep,
  isNil as _isNil,
} from 'lodash';

import { StringUtils } from '@blcloud/bl-common';
import {
  IDmSnsMsgSetting,
  IOperationHistoryLog,
  ISnsBulkSendHistory,
  ISnsDeliveryRequest,
} from '@blcloud/bl-datamodel';
import { ProductCode } from '@blcloud/bl-datamodel/enum/common/product-code';
import { OpeHistoryKindDiv } from '@blcloud/bl-datamodel/enum/bizcmn/ope-history-kind-div';
import { OperationContentDiv } from '@blcloud/bl-datamodel/enum/bizcmn/operation-content-div';
import { DmExtractMenuDivMap } from '@blcloud/bl-datamodel/enum/approach/dm-extract-menu-div';
import { SnsSendPoint } from '@blcloud/bl-datamodel/enum/sns/sns-send-point';
import { RoleConst } from '@blcloud/bl-datamodel/const/role';
import {
  DmSurveyUtil,
  PageId,
  LoginResourceService
} from '@blcloud/bl-ng-share-module';
import {
  OperationHistoryLogResource, SnsBulkSendHistoryResource,
  SnsDeliveryRequestResource,
} from '@blcloud/bl-ng-resource';

import { ISnsSendMessageInfo } from './sns-send-message-confirm-modal.interface';

/**
 * 送信確認モーダル サービス
 */
@Injectable()
export class SnsSendMessageConfirmModalService {

  /** コンストラクタ */
  constructor(
    private operationHistoryLogResource: OperationHistoryLogResource,
    private snsDeliveryRequestResource: SnsDeliveryRequestResource,
    private snsBulkSendHistoryResource: SnsBulkSendHistoryResource,
    private loginResourceService: LoginResourceService,
  ) {
  }

  /**
   * SNS配信
   * @param param SNS配信リクエスト
   * @param option
   * @return 登録結果
   */
  postSnsDeliveryRequest(param, option): Observable<any> {
    return this.snsDeliveryRequestResource.post<ISnsDeliveryRequest>(param, option);
  }

  /**
   * LINE一括送信の操作履歴ログを登録
   */
  postOperationHistoryLogLineBulkSend(dmExtractMenuDiv: string, snsSendTitle: string) {
    const dmExtractMenuName = DmExtractMenuDivMap[dmExtractMenuDiv];
    const opeHistoryMessage =
      StringUtils.concat(
        '『[',
        dmExtractMenuName,
        ']',
        snsSendTitle,
        '』のLINEを送信しました。',
      );

    const entity: IOperationHistoryLog = {
      // プロダクトコード
      productCode: ProductCode.Common,
      // 操作履歴種別区分
      opeHistoryKindDiv: OpeHistoryKindDiv.Record,
      // 画面ID
      pageId: PageId.LineBulkSend,
      // 操作履歴機能名称
      opeHistoryFunctionName: 'LINE一斉送信',
      // 操作内容区分
      operationContentDiv: OperationContentDiv.Send,
      // 操作履歴メッセージ配列
      opeHistoryMessageList: [opeHistoryMessage],
      // サブジェクト
      subjects: {
        sf: {}
      }
    };

    this.operationHistoryLogResource.post<IOperationHistoryLog>(entity).pipe(
      catchError(() => {
        return ObservableOf(null);
      })
    ).subscribe();
  }

  /**
   * LINE一斉送信のロールを取得
   */
  isAvailableLineBulkSendRole(): Observable<boolean> {
    return this.loginResourceService.isAvailableFunction(RoleConst.ROLEID_FUNC_ALL_LINE_BULK_SEND);
  }

  /**
   * LINEメッセージプレビューデータ作成
   */
  createPreview(snsSendMessageInfo: ISnsSendMessageInfo): Observable<string[]> {
    const surveyNo = this.toSendSurveyNo(snsSendMessageInfo.surveyNo);
    const dmSnsMsgSettingList = this.buildSyncedDmSnsMsgSettingList(snsSendMessageInfo);
    return this.loginResourceService.getLoginUserEmployeeBindModel().pipe(
      mergeMap(loginUser => {
        const body = {
          blTenantId: loginUser.blTenantId,
          snsDiv: snsSendMessageInfo.snsDiv,
          snsSendPoint: SnsSendPoint.FriendSend,
          ticketCodeList: snsSendMessageInfo.ticketCodeList,
          dmContentNumber: snsSendMessageInfo.dmContentNumber,
          dmContentName: snsSendMessageInfo.dmContentName,
          dmExtractMenuCode: snsSendMessageInfo.dmExtractMenuCode,
          dmExtractMenuDiv: snsSendMessageInfo.dmExtractMenuDiv,
          dmContentTitle: snsSendMessageInfo.dmContentTitle,
          messageContents: snsSendMessageInfo.dmContentBody,
          dmSnsMsgSettingList: dmSnsMsgSettingList,
          surveyNo: surveyNo,
        } as ISnsBulkSendHistory;
        return this.snsBulkSendHistoryResource.post(body, {entityOptions: {exPath: 'preview'}});
      })
    );
  }

  /**
   * プレビュー・送信用の吹き出し設定を作る
   * 画面上の案内文は変更しない
   * アンケート番号に合わせて吹き出しを同期する（未選択なら除去、変更なら更新、無ければ追加）
   * @param snsSendMessageInfo SNS送信メッセージ情報
   */
  private buildSyncedDmSnsMsgSettingList(snsSendMessageInfo: ISnsSendMessageInfo): IDmSnsMsgSetting[] {
    const surveyNo = this.toSendSurveyNo(snsSendMessageInfo.surveyNo);
    const content = _cloneDeep(snsSendMessageInfo.dmContentInfo);
    if (_isNil(content)) {
      return [];
    }
    DmSurveyUtil.syncSurveySettingBySurveyNo(
      content,
      surveyNo,
      snsSendMessageInfo.surveyContentTitle || '',
      snsSendMessageInfo.surveyContentBody || '',
      snsSendMessageInfo.snsShopAccountSetting
    );
    DmSurveyUtil.applySurveyLiffUrlToSettings(
      content.dmSnsMsgSettingList,
      snsSendMessageInfo.snsShopAccountSetting
    );
    return content.dmSnsMsgSettingList || [];
  }

  /**
   * 送信に渡すアンケート番号と吹き出し設定を作る
   * プレビューと同じ同期結果を返す。画面上の案内文は変更しない
   * @param snsSendMessageInfo SNS送信メッセージ情報
   */
  buildSendSurveyPayload(snsSendMessageInfo: ISnsSendMessageInfo): {
    surveyNo: number | null,
    dmSnsMsgSettingList: IDmSnsMsgSetting[]
  } {
    const surveyNo = this.toSendSurveyNo(snsSendMessageInfo.surveyNo);
    return {
      surveyNo,
      dmSnsMsgSettingList: this.buildSyncedDmSnsMsgSettingList(snsSendMessageInfo),
    };
  }

  /**
   * 送信するアンケート番号を返す。未選択はnull
   * @param surveyNo アンケート番号
   */
  private toSendSurveyNo(surveyNo: number | null | undefined): number | null {
    if (surveyNo == null || surveyNo === 0) {
      return null;
    }
    return surveyNo;
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/sns-send-message-confirm-modal/sns-send-message-confirm-modal.service.ts